import React, { useState, useEffect } from 'react';
import { productionAPI, subBrandAPI, productAPI } from '../../api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, Lock, Unlock, Save, Upload, Trash2 } from 'lucide-react';
import { useConfirm } from '@/components/ConfirmProvider';

// ============ PRODUCTION PLANNER (LOT SHEETS) ============
// Each lot sheet = one (facility × parent_product × date × lot_no). Each lot
// renders as its own card (no parent-product stacking). Header carries S.No,
// Seq, Lot N/M, Parent, Facility, Batches, Expected, and the action buttons.
// Body table: Sub Product (dropdown) | Wt | Variant | Actual Production.

export const ProductionTab = ({ refreshKey = 0, drillFilter, clearDrillFilter } = {}) => {
  const confirm = useConfirm();
  const todayISO = () => new Date().toISOString().slice(0, 10);
  // Default date is ALWAYS today on every fresh mount (per user request — 12/Feb/2026).
  // The Planning tab's `vikas:lastPlannerSyncDate` localStorage hint is intentionally
  // ignored here so operators always land on today's lots.
  const [date, setDate] = useState(todayISO());
  const [facility, setFacility] = useState('all');
  // Manual lot-creation dialog state
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ parent_product_id: '', facility: 'E-302', total_batches: 0 });
  const [createBusy, setCreateBusy] = useState(false);
  const [sheets, setSheets] = useState([]);
  // Deep-link target: when Stock & Delivery's Production popover clicks a Lot #,
  // we get drillFilter.focus_sheet_id and need to scroll to that sheet, expand
  // it, and apply a brief amber highlight.
  const [focusedSheetId, setFocusedSheetId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState({}); // { sheet_id: rows[] }
  // Header-editable values per sheet — Batches and Seq live up here now, not per-row.
  const [headerDraft, setHeaderDraft] = useState({}); // { sheet_id: { total_batches_planned, seq_no } }
  const [busy, setBusy] = useState({});
  const [subBrands, setSubBrands] = useState([]);
  const [products, setProducts] = useState([]);
  // Expanded/collapsed state per sheet — collapsed by default; click Row 1 to expand
  // and reveal the Save / Sync / Close action buttons + the Products / Actual / Wt /
  // Variant form table. Until expanded, only the single header row is visible.
  const [expanded, setExpanded] = useState({});
  // Planned bags per (product_id, facility) for the active date — sourced from
  // `plan_overrides`. Editable via the inline Plan input in each lot card; persisted
  // back to the same collection on blur.
  const [planMap, setPlanMap] = useState({});

  // Fetch sub-brands master once for the Sub Product dropdown
  useEffect(() => {
    subBrandAPI.getAll().then(r => setSubBrands(r.data || [])).catch(() => {});
    productAPI.getAll().then(r => setProducts(r.data || [])).catch(() => {});
  }, []);

  // Batch size for a (parent_product, facility) — used to compute Expected.
  const batchSizeFor = (parentId, fac) => {
    const p = products.find(pp => pp.id === parentId);
    if (!p) return null;
    const s = fac === 'E-302' ? p.batch_size_e302 : p.batch_size_e303;
    return s && Number(s) > 0 ? Number(s) : null;
  };

  // Monotonic counter — drops stale concurrent fetches (race guard).
  const loadSeqRef = React.useRef(0);
  const load = async () => {
    const seq = ++loadSeqRef.current;
    setLoading(true);
    try {
      const params = { date_from: date, date_to: date };
      if (facility !== 'all') params.facility = facility;
      // Run lot-sheet load and plan-overrides fetch in parallel so the Plan
      // column populates without an extra round-trip.
      const [r, ovRes] = await Promise.all([
        productionAPI.listLotSheets(params),
        productionAPI.getPlanOverrides(date).catch(() => ({ data: [] })),
      ]);
      if (seq !== loadSeqRef.current) return;  // stale — drop, don't touch state
      const list = r.data.sheets || [];
      setSheets(list);
      // Build the planMap: { product_id: { 'E-302': N, 'E-303': N } }
      // The /production/plan/overrides endpoint returns
      // `{ date, overrides: { product_id: { 'E-302': N, 'E-303': N } } }` —
      // so we read `data.overrides`, not `data` itself.
      const pm = { ...(ovRes?.data?.overrides || {}) };
      setPlanMap(pm);
      // Hydrate body-rows and header-values from the server payload. If a
      // sheet has no rows yet, seed a single default row with the parent
      // product pre-selected — saves the user a click and reflects the most
      // common case (one parent-product entry per lot).
      const d = {};
      const h = {};
      list.forEach((s) => {
        // Hydrate body-rows from server. For backward compat: any persisted
        // row that has no `sub_brand_name` (legacy empty placeholder) gets the
        // parent product as its default selection so the dropdown isn't
        // stranded on "— Select Product —" when the operator first opens the
        // lot. If there are no persisted rows at all, seed a single default
        // row pre-populated with the parent product.
        const persistedRows = (s.rows || []).map((row) => ({ ...row }));
        const defaultRow = {
          product_id: s.parent_product_id,
          product_name: s.parent_product_name,
          sub_brand_name: s.parent_product_name,
          weight_kg: null,
          variant: null,
          actual_production: 0,
          plan: 0,
        };
        if (persistedRows.length === 0) {
          d[s.id] = [defaultRow];
        } else {
          // Normalize a missing sub-brand on the FIRST row only — typical
          // when the planner auto-creates an empty row for a fresh lot.
          if (!persistedRows[0].sub_brand_name) {
            persistedRows[0] = { ...persistedRows[0], sub_brand_name: s.parent_product_name };
          }
          d[s.id] = persistedRows;
        }
        h[s.id] = {
          total_batches_planned: Number(s.total_batches_planned) || 0,
          // Header Seq: prefer the lot-sheet's own persisted seq_no (set via
          // PUT /production/lot-sheets/{id}/seq), then fall back to the first
          // row's seq_no, then lot_no. We also track whether this value came
          // from a real persisted seq_no or a fallback so the per-facility
          // dedup pass below knows which sheets are safe to renumber.
          seq_no: Number(s.seq_no) || Number((s.rows || [])[0]?.seq_no) || s.lot_no || 1,
          _seqPersisted: !!Number(s.seq_no),
        };
      });
      // Resolve per-facility duplicate Seq values. Each parent product has its
      // own `lot_no` sequence starting from 1, so the fallback path can
      // produce collisions. Older bug data may also contain duplicate
      // *persisted* seq_no values (e.g., MOTICHOOR seq=2 + SONA SIKKA seq=2).
      // We resolve in this priority order so the renumber is deterministic:
      //   1. Lots with the LOWEST lot_no keep their seq_no first
      //   2. Subsequent lots that would clash get the lowest unused seq
      // The new seq is persisted to the backend so the next reload is stable.
      // Lots with explicit `_seqPersisted` AND no clash are preserved exactly.
      const byFacility = {};
      list.forEach((s) => {
        (byFacility[s.facility] = byFacility[s.facility] || []).push(s.id);
      });
      const sheetById = Object.fromEntries(list.map((s) => [s.id, s]));
      const persistQueue = [];
      Object.values(byFacility).forEach((idsInFac) => {
        // Stable order: persisted seqs first (in ascending seq order), then
        // fallback ones (also ascending). Within ties, lower lot_no wins.
        const ordered = idsInFac.slice().sort((a, b) => {
          const ap = h[a]._seqPersisted ? 0 : 1;
          const bp = h[b]._seqPersisted ? 0 : 1;
          if (ap !== bp) return ap - bp;
          if (h[a].seq_no !== h[b].seq_no) return h[a].seq_no - h[b].seq_no;
          return (sheetById[a]?.lot_no || 0) - (sheetById[b]?.lot_no || 0);
        });
        const used = new Set();
        ordered.forEach((id) => {
          let v = h[id].seq_no;
          if (used.has(v) || v < 1) {
            let free = 1;
            while (used.has(free)) free += 1;
            v = free;
          }
          if (v !== h[id].seq_no || !h[id]._seqPersisted) {
            // Persist to backend either because the value changed, or because
            // this is a fresh fallback that hasn't been written yet — so the
            // next reload is fully stable.
            if (v !== h[id].seq_no) persistQueue.push({ id, seq: v });
            else if (!h[id]._seqPersisted) persistQueue.push({ id, seq: v });
          }
          h[id].seq_no = v;
          used.add(v);
        });
      });
      setDraft(d);
      setHeaderDraft(h);
      // Fire-and-forget persistence of any renumbered/fallback seqs so the
      // next reload is fully stable. Swallow errors silently — optimistic
      // local state is correct either way; the next commitSeq will retry.
      if (persistQueue.length) {
        Promise.all(
          persistQueue.map(({ id, seq }) =>
            productionAPI.updateLotSheetSeq(id, seq).catch(() => null)
          )
        );
      }
    } catch (e) {
      if (seq === loadSeqRef.current) toast.error('Failed to load lot sheets');
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date, facility]);

  // Silent refresh when the parent bumps `refreshKey` (e.g., user navigates back
  // to this tab). Skips the initial render — the [date, facility] effect above
  // already fetches on first mount. Reload() does NOT reset scroll position,
  // expanded lots, or any user-edited form values (those live in headerDraft /
  // draft state which is independent of the fetch payload).
  const isFirstRefreshRef = React.useRef(true);
  useEffect(() => {
    if (isFirstRefreshRef.current) { isFirstRefreshRef.current = false; return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  // Deep-link from Stock & Delivery → Production popover. When a lot # is
  // clicked there, the parent passes drillFilter.focus_sheet_id here. We need
  // to:
  //   1. Switch the date filter to the sheet's date (sheets are scoped per day)
  //      so the sheet renders.
  //   2. Wait for the load to complete, then expand + scroll + highlight.
  React.useEffect(() => {
    const sid = drillFilter?.focus_sheet_id;
    if (!sid) return;
    // If the sheet is already in the loaded list, jump to it directly.
    const found = sheets.find((s) => s.id === sid);
    if (found) {
      if (found.facility && facility !== 'ALL' && found.facility !== facility) {
        setFacility('ALL');
      }
      setExpanded((prev) => ({ ...prev, [sid]: true }));
      setFocusedSheetId(sid);
      setTimeout(() => {
        const el = document.querySelector(`[data-sheet-id="${sid}"]`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 250);
      setTimeout(() => { setFocusedSheetId(null); if (clearDrillFilter) clearDrillFilter(); }, 4000);
      return;
    }
    // Otherwise fetch the sheet's date and switch to it. The next load() will
    // pick it up and this effect will re-run with `found` populated.
    (async () => {
      try {
        const r = await productionAPI.getLotSheet(sid);
        const d = r?.data?.date;
        if (d) setDate(d);
        // After load completes, this effect re-runs because sheets state updates.
      } catch (e) {
        toast.error('Could not locate that lot sheet');
        if (clearDrillFilter) clearDrillFilter();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drillFilter?.focus_sheet_id, sheets]);

  const setRow = (sheetId, idx, key, value) => {
    setDraft((prev) => ({
      ...prev,
      [sheetId]: (prev[sheetId] || []).map((r, i) => i === idx ? { ...r, [key]: value } : r),
    }));
  };

  const setHeader = (sheetId, key, value) => {
    setHeaderDraft((prev) => ({
      ...prev,
      [sheetId]: { ...(prev[sheetId] || {}), [key]: value },
    }));
  };

  // When the user finishes typing into a Seq input, ensure uniqueness WITHIN
  // the same facility (E-302 / E-303 each have their own 1, 2, 3... sequence).
  // If another lot IN THE SAME FACILITY already has the new value, SWAP their
  // seq numbers so both stay distinct. Empty / <1 values snap to the next free
  // seq within that facility.
  const commitSeq = async (sheetId) => {
    const me = sheets.find((s) => s.id === sheetId);
    const myFac = me?.facility;
    if (!myFac) return;
    const target = Math.max(1, Number(headerDraft[sheetId]?.seq_no) || 0);
    // Optimistic local swap so the visible order updates immediately.
    const idsInFac = sheets.filter((s) => s.facility === myFac).map((s) => s.id);
    let otherId = null;
    let otherFreeSeq = null;
    setHeaderDraft((prev) => {
      const next = { ...prev };
      const other = idsInFac.find((id) => id !== sheetId && Number(next[id]?.seq_no) === target);
      if (other) {
        const used = new Set(idsInFac
          .filter((id) => id !== other)
          .map((id) => Number(next[id]?.seq_no) || 0));
        let free = 1;
        while (used.has(free)) free += 1;
        next[other] = { ...(next[other] || {}), seq_no: free };
        otherId = other;
        otherFreeSeq = free;
      }
      next[sheetId] = { ...(next[sheetId] || {}), seq_no: target };
      return next;
    });
    // Persist on the backend. CRITICAL: when a local swap was needed, the
    // *other* lot must be persisted FIRST so that the backend's own
    // conflict-detection (which only looks at the `seq_no` field, not the
    // `lot_no` fallback used by the frontend) doesn't see a phantom
    // conflict when we set self to `target`. Without this two-step persist,
    // fresh lots whose `seq_no` was never explicitly written would silently
    // keep their fallback `lot_no` value, producing duplicate Seq numbers
    // visible to the operator after a reload (bug reported 19/Feb/2026).
    try {
      if (otherId !== null && otherFreeSeq !== null) {
        await productionAPI.updateLotSheetSeq(otherId, otherFreeSeq);
      }
      await productionAPI.updateLotSheetSeq(sheetId, target);
    } catch (e) {
      toast.error(`Failed to save Seq: ${e?.response?.data?.detail || e.message}`);
    }
  };

  // Update the locally-cached Plan (bags) for a given (parent_product, facility).
  // We update the in-memory map immediately so the input feels responsive, and
  // persist to `plan_overrides` on blur via `commitPlan` below.
  const setPlan = (productId, fac, raw) => {
    const n = raw === '' ? 0 : Math.max(0, Number(raw) || 0);
    setPlanMap((prev) => ({
      ...prev,
      [productId]: { ...(prev[productId] || {}), [fac]: n },
    }));
  };
  const commitPlan = async (productId) => {
    try {
      const by_facility = planMap[productId] || {};
      await productionAPI.savePlanOverride({ date, product_id: productId, by_facility });
    } catch (e) {
      toast.error('Failed to save Plan');
    }
  };

  const toggleExpand = (sheetId) => {
    setExpanded((prev) => ({ ...prev, [sheetId]: !prev[sheetId] }));
  };

  // Manual lot creation: POSTs to /api/production/lot-sheets and reloads.
  const submitCreate = async () => {
    if (!createForm.parent_product_id) {
      toast.error('Pick a parent product');
      return;
    }
    setCreateBusy(true);
    try {
      const res = await productionAPI.createLotSheet({
        date,
        facility: createForm.facility,
        parent_product_id: createForm.parent_product_id,
        total_batches: Number(createForm.total_batches) || 0,
      });
      toast.success(`Lot ${res.data?.lot_no || ''} created`);
      setCreateOpen(false);
      // Auto-expand the brand-new sheet so the operator can fill it immediately
      if (res.data?.id) setExpanded((prev) => ({ ...prev, [res.data.id]: true }));
      await load();
    } catch (e) {
      toast.error(`Create failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setCreateBusy(false); }
  };

  const addRow = (sheet) => {
    setDraft((prev) => {
      const cur = prev[sheet.id] || [];
      return {
        ...prev,
        [sheet.id]: [...cur, {
          // Body rows share the lot's header Seq; we still persist a value per row
          // so the existing /sheets schema and flat-Seq view keep working.
          seq_no: headerDraft[sheet.id]?.seq_no || 1,
          // Default to the lot's parent product — that's the MAIN product the
          // lot was created for (same product name surfaced in Stock & Product
          // Summary tabs). Operators can still switch to a sub-brand from the
          // dropdown when this lot is producing a variant.
          sub_brand_name: sheet.parent_product_name || '',
          weight_kg: 50,
          variant: null,
          _weight_edit: false,
          _variant_edit: false,
          batches: 0,
          actual_production: 0,
        }],
      };
    });
  };

  const removeRow = (sheetId, idx) => {
    setDraft((prev) => ({
      ...prev,
      [sheetId]: (prev[sheetId] || []).filter((_, i) => i !== idx),
    }));
  };

  const buildPayload = (sheet) => {
    const head = headerDraft[sheet.id] || {};
    const headerSeq = Number(head.seq_no) || sheet.lot_no || 1;
    const rows = (draft[sheet.id] || []).map((r) => ({
      // Every body row inherits the lot's header Seq so the flat-seq view treats
      // them as one group; weights/variants still persist per row.
      seq_no: headerSeq,
      sub_brand_name: r.sub_brand_name || null,
      weight_kg: r.weight_kg == null || r.weight_kg === '' ? null : Number(r.weight_kg),
      variant: r.variant || null,
      batches: 0, // batches moved to header; per-row batches no longer used
      actual_production: Number(r.actual_production) || 0,
      // Per-row Plan — independent of other rows. Stored on the lot sheet
      // row so it persists across reloads without touching plan_overrides.
      plan: r.plan === '' || r.plan == null ? 0 : Number(r.plan),
    }));
    return { rows, total_batches_planned: Number(head.total_batches_planned) || 0 };
  };

  const saveSheet = async (sheet) => {
    setBusy((b) => ({ ...b, [sheet.id]: true }));
    try {
      await productionAPI.updateLotSheet(sheet.id, buildPayload(sheet));
      toast.success('Saved');
      // Background refetch — don't block the toast / UI release. The
      // load() seq guard ensures any in-flight stale response is dropped.
      load();
    } catch (e) {
      toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setBusy((b) => ({ ...b, [sheet.id]: false })); }
  };

  const syncToStock = async (sheet) => {
    setBusy((b) => ({ ...b, [sheet.id]: true }));
    try {
      await productionAPI.updateLotSheet(sheet.id, buildPayload(sheet));
      await productionAPI.syncLotSheetToStock(sheet.id);
      toast.success('Synced to stock');
      load();
    } catch (e) {
      toast.error(`Sync failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setBusy((b) => ({ ...b, [sheet.id]: false })); }
  };

  const closeSheet = async (sheet) => {
    const ok = await confirm({
      title: `Close Lot ${sheet.lot_no} — ${sheet.parent_product_name}?`,
      description: 'This locks the sheet and posts any unsynced actual production to stock. You can reopen it later if needed.',
      confirmText: 'Close & lock',
    });
    if (!ok) return;
    setBusy((b) => ({ ...b, [sheet.id]: true }));
    try {
      await productionAPI.updateLotSheet(sheet.id, buildPayload(sheet));
      await productionAPI.closeLotSheet(sheet.id);
      toast.success('Sheet closed');
      load();
    } catch (e) {
      toast.error(`Close failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setBusy((b) => ({ ...b, [sheet.id]: false })); }
  };

  const reopenSheet = async (sheet) => {
    const ok = await confirm({ title: `Reopen Lot ${sheet.lot_no}?`, description: 'You will be able to edit rows again. Stock already posted will not be reversed.', confirmText: 'Reopen' });
    if (!ok) return;
    setBusy((b) => ({ ...b, [sheet.id]: true }));
    try {
      await productionAPI.reopenLotSheet(sheet.id);
      toast.success('Reopened');
      await load();
    } catch (e) {
      toast.error(`Reopen failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setBusy((b) => ({ ...b, [sheet.id]: false })); }
  };

  // Delete a lot sheet. Backend refuses if stock has already been synced —
  // keeps inventory numbers safe. Remaining sibling lots auto-renumber.
  const deleteSheet = async (sheet) => {
    const ok = await confirm({
      title: `Delete Lot ${sheet.lot_no} — ${sheet.parent_product_name}?`,
      description: 'This permanently removes the lot sheet. Refused if any actual production has already been synced to stock.',
      confirmText: 'Delete',
    });
    if (!ok) return;
    setBusy((b) => ({ ...b, [sheet.id]: true }));
    try {
      await productionAPI.deleteLotSheet(sheet.id);
      toast.success('Deleted');
      await load();
    } catch (e) {
      toast.error(`Delete failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setBusy((b) => ({ ...b, [sheet.id]: false })); }
  };

  // Each sheet is its own card; we only bucket by facility for the section
  // header when "All" is selected. No more parent-product grouping. Sheets within
  // each bucket are auto-arranged by their header Seq No (lowest first).
  const seqOf = (s) => Number(headerDraft[s.id]?.seq_no) || s.lot_no || 0;
  const facilityBuckets = React.useMemo(() => {
    if (facility !== 'all') return null;
    const order = ['E-302', 'E-303'];
    const present = Array.from(new Set(sheets.map((s) => s.facility)));
    const others = present.filter((f) => !order.includes(f)).sort();
    const ordered = [...order, ...others].filter((f) => present.includes(f));
    return ordered.map((f) => ({
      facility: f,
      sheets: sheets
        .filter((s) => s.facility === f)
        .slice()
        .sort((a, b) => seqOf(a) - seqOf(b)),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sheets, facility, headerDraft]);

  // Sub Product dropdown helper (uses sub-brand master)
  const subBrandOptions = React.useMemo(
    () => [...subBrands].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [subBrands]
  );

  // ====== Single LotSheet card renderer ======
  const renderLotSheet = (sheet) => {
    const rows = draft[sheet.id] || [];
    const head = headerDraft[sheet.id] || { total_batches_planned: 0, seq_no: sheet.lot_no };
    const isClosed = sheet.status === 'closed';
    const isSheetBusy = !!busy[sheet.id];
    const bs = batchSizeFor(sheet.parent_product_id, sheet.facility);
    const batches = Number(head.total_batches_planned) || 0;
    const expected = bs ? batches * bs : null;
    const sumActual = rows.reduce((s, r) => s + (Number(r.actual_production) || 0), 0);
    const isOpen = !!expanded[sheet.id];

    return (
      <Card key={sheet.id} data-sheet-id={sheet.id} className="overflow-hidden" data-testid="prod-sheet" style={{ opacity: isClosed ? 0.85 : 1, background: focusedSheetId === sheet.id ? '#FFF3E0' : undefined, transition: 'background 0.4s' }}>
        {/* ROW 1 — single-line summary, the ONLY thing visible when collapsed.
            Per the 12/Feb/2026 redesign:
              Seq (bold) | Parent name | Batches (bold) | Actual: N bags
            Removed: Lot N of M, facility chip (already in section header),
            middle-dot separators. Action buttons moved to the footer next to
            "+ Add sub-brand row" so the second always-visible row could be
            dropped entirely. Inputs stop click propagation so editing them
            doesn't toggle the card. */}
        <div
          className="flex items-center gap-2 sm:gap-3 flex-wrap px-3 py-2 text-xs cursor-pointer hover:bg-[#FBF9F6] min-h-[44px]"
          style={{ borderColor: '#E2DFD8', background: isOpen ? '#FBF9F6' : '#FFFFFF', color: '#5C6B61', borderBottom: isOpen ? '1px solid #E2DFD8' : 'none' }}
          onClick={() => toggleExpand(sheet.id)}
          role="button"
          tabIndex={0}
          title={isOpen ? 'Tap to collapse' : 'Tap to expand the form'}
          data-testid="prod-sheet-row1"
        >
          <label className="flex items-center" title="Sequence — unique within this facility. Drives Sort by Seq order. Lots auto-arrange ascending after every edit." onClick={(e) => e.stopPropagation()}>
            <Input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={head.seq_no || ''}
              onChange={(e) => {
                const v = e.target.value;
                if (v === '' || /^[0-9]+$/.test(v)) setHeader(sheet.id, 'seq_no', v);
              }}
              onFocus={(e) => e.target.select()}
              onBlur={() => commitSeq(sheet.id)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.currentTarget.blur(); } }}
              disabled={isClosed}
              className="h-8 w-12 text-xs text-center font-bold"
              data-testid="prod-header-seq"
            />
          </label>
          <b className="text-sm" style={{ color: '#2B5339' }}>{sheet.parent_product_name}</b>
          <label className="flex items-center gap-1.5" title="Number of batches planned for this lot" onClick={(e) => e.stopPropagation()}>
            <span>Batches:</span>
            <Input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={head.total_batches_planned ?? 0}
              onChange={(e) => {
                const v = e.target.value;
                if (v === '' || /^[0-9]+$/.test(v)) setHeader(sheet.id, 'total_batches_planned', v);
              }}
              onFocus={(e) => e.target.select()}
              disabled={isClosed}
              className="h-8 w-12 text-xs text-center font-bold"
              data-testid="prod-header-batches"
            />
          </label>
          <span className="text-[11px]" title="Sum of Actual Production across all sub-brand rows in this lot.">
            Actual: <b style={{ color: '#2B5339' }}>{sumActual}</b> bags
          </span>
          {isClosed && <span className="text-xs px-1.5 py-0.5 rounded font-bold ml-auto" style={{ background: '#5C6B61', color: 'white' }}><Lock className="w-3 h-3 inline mr-1" />Closed</span>}
        </div>

        {/* The previously-separate "Actions Row" (Actual + Save/Sync/Close/Delete)
            was deleted on 12/Feb/2026. Actual now lives on Row 1; action buttons
            moved to the form footer next to "+ Add sub-brand row". */}

        {/* FORM TABLE: Products (dropdown) | Actual Production | Wt (MISC-style) | Variant (MISC-style) | ×
            Collapsed by default — click Row 1 to expand. Auto-width table so
            columns hug their content and stay packed to the left. The wrapper
            is `w-fit max-w-full` so both the table and the colored "+ Add sub-brand
            row" footer crop to the natural width of the columns instead of
            stretching across the full Card width (which was leaving blank
            page space to the right after the columns were narrowed). */}
        {isOpen && (
        <>
        <div className="overflow-x-auto w-fit max-w-full">
          <table className="text-sm border-collapse" style={{ width: 'auto' }}>
            <thead>
              <tr style={{ background: '#FBF9F6' }}>
                <th className="px-2 py-1.5 text-left">Products</th>
                <th className="px-2 py-1.5 text-center" title="Planned bags for this product at this facility for this date. Shared across all rows in this lot.">Plan</th>
                <th className="pl-2 pr-0 py-1.5 text-center" style={{ color: '#2B5339' }}>Actual</th>
                <th className="pl-0 pr-2 py-1.5 text-center">Wt.</th>
                <th className="px-2 py-1.5 text-left">Variant</th>
                <th className="px-1 py-1.5 text-center w-8"></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan="6" className="p-3 text-center" style={{ color: '#5C6B61' }}>No sub-brand rows yet. Click <b>+ Add sub-brand row</b> below.</td></tr>
              ) : rows.map((r, idx) => {
                const wtIsCustom = r._weight_edit || ![40, 45, 50, 60].includes(Number(r.weight_kg));
                const varIsCustom = r._variant_edit || !['', 'GUR', 'SPL', null].includes(r.variant || '');
                return (
                  <tr key={idx} className="border-t" style={{ borderColor: '#E2DFD8' }}>
                    {/* Products — dropdown. The parent product is the MAIN
                        product (the one tracked in Stock & Product Summary);
                        sub-brands are variants of it. Parent is presented as
                        the default first option (no "(parent)" suffix — it
                        IS the main product). */}
                    <td className="px-2 py-1.5">
                      <select
                        value={r.sub_brand_name || ''}
                        onChange={(e) => setRow(sheet.id, idx, 'sub_brand_name', e.target.value)}
                        disabled={isClosed}
                        className="px-2 py-1.5 border rounded text-sm bg-white"
                        style={{ borderColor: '#E2DFD8' }}
                        data-testid="prod-row-sub-select"
                      >
                        <option value={sheet.parent_product_name}>{sheet.parent_product_name}</option>
                        {subBrandOptions
                          .filter(sb => sb.name !== sheet.parent_product_name)
                          .map(sb => (
                            <option key={sb.id} value={sb.name}>↳ {sb.name}</option>
                          ))}
                      </select>
                    </td>
                    {/* Plan — per-row independent value stored on the lot
                        sheet row. Persisted via the lot sheet save (no
                        coupling to the parent product's plan_overrides), so
                        editing one row's Plan does NOT propagate to sibling
                        rows. Text input with numeric mode — no spinner,
                        click selects the value. */}
                    <td className="px-2 py-1.5 text-center">
                      <Input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={r.plan == null ? '' : r.plan}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === '' || /^[0-9]+$/.test(v)) setRow(sheet.id, idx, 'plan', v === '' ? '' : Number(v));
                        }}
                        onFocus={(e) => e.target.select()}
                        disabled={isClosed}
                        className="h-8 w-16 text-center text-sm"
                        data-testid="prod-row-plan"
                        title="Planned bags for this row. Independent — editing this does not affect other sub-brand rows in the lot."
                      />
                    </td>
                    {/* Actual Production — text input (no spinner arrows)
                        with select-on-focus so click highlights the current
                        value for fast overwrite. */}
                    <td className="pl-2 pr-0 py-1.5 text-right">
                      <Input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={r.actual_production ?? 0}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === '' || /^[0-9]+$/.test(v)) setRow(sheet.id, idx, 'actual_production', v === '' ? 0 : Number(v));
                        }}
                        onFocus={(e) => e.target.select()}
                        disabled={isClosed}
                        className="h-8 w-16 text-right text-sm font-bold"
                        style={{ color: '#2B5339' }}
                        data-testid="prod-row-actual"
                      />
                    </td>
                    {/* Wt — MISC-style: 40 / 45 / 50 / 60 / Edit… */}
                    <td className="pl-0 pr-2 py-1.5">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <select
                          value={[40, 45, 50, 60].includes(Number(r.weight_kg)) && !r._weight_edit ? String(r.weight_kg) : 'edit'}
                          onChange={(e) => {
                            const v = e.target.value;
                            if (v === 'edit') setRow(sheet.id, idx, '_weight_edit', true);
                            else { setRow(sheet.id, idx, '_weight_edit', false); setRow(sheet.id, idx, 'weight_kg', Number(v)); }
                          }}
                          disabled={isClosed}
                          className="px-2 py-1 border rounded text-sm bg-white"
                          style={{ borderColor: '#E2DFD8' }}
                          data-testid="prod-row-wt-select"
                        >
                          <option value="40">40 kg</option>
                          <option value="45">45 kg</option>
                          <option value="50">50 kg</option>
                          <option value="60">60 kg</option>
                          <option value="edit">Edit…</option>
                        </select>
                        {wtIsCustom && (
                          <Input
                            type="number"
                            min="1"
                            placeholder="kg"
                            value={r.weight_kg ?? ''}
                            onChange={(e) => setRow(sheet.id, idx, 'weight_kg', e.target.value)}
                            disabled={isClosed}
                            className="h-8 w-14 text-sm"
                            data-testid="prod-row-wt-custom"
                          />
                        )}
                      </div>
                    </td>
                    {/* Variant — MISC-style: '' / GUR / SPL / Edit… */}
                    <td className="px-2 py-1.5">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <select
                          value={['', 'GUR', 'SPL'].includes(r.variant || '') && !r._variant_edit ? (r.variant || '') : 'edit'}
                          onChange={(e) => {
                            const v = e.target.value;
                            if (v === 'edit') setRow(sheet.id, idx, '_variant_edit', true);
                            else { setRow(sheet.id, idx, '_variant_edit', false); setRow(sheet.id, idx, 'variant', v || null); }
                          }}
                          disabled={isClosed}
                          className="px-2 py-1 border rounded text-sm bg-white"
                          style={{ borderColor: '#E2DFD8' }}
                          data-testid="prod-row-variant-select"
                        >
                          <option value="">—</option>
                          <option value="GUR">GUR</option>
                          <option value="SPL">SPL</option>
                          <option value="edit">Edit…</option>
                        </select>
                        {varIsCustom && (
                          <Input
                            type="text"
                            placeholder="Variant"
                            value={r.variant || ''}
                            onChange={(e) => setRow(sheet.id, idx, 'variant', e.target.value.toUpperCase())}
                            disabled={isClosed}
                            className="h-8 w-16 text-sm"
                            data-testid="prod-row-variant-custom"
                          />
                        )}
                      </div>
                    </td>
                    <td className="px-1 py-1.5 text-center">
                      {!isClosed && (
                        <button
                          type="button"
                          onClick={() => removeRow(sheet.id, idx)}
                          className="text-xs px-1.5 py-0.5 rounded hover:bg-[#FBF9F6]"
                          style={{ color: '#C05C35' }}
                          title="Remove row"
                          data-testid="prod-row-remove"
                        >×</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
          {/* FOOTER ROW — last row inside the lot card. Holds the "+ Add
              sub-brand row" button and lot-level action buttons (Save /
              Sync / Close / Delete or Reopen) packed together on the left.
              Replaces the old standalone "Actions Row". Placed outside the
              table's w-fit wrapper so it spans the full card width. */}
          <div
            className="flex items-center flex-wrap gap-2 p-2 border-t"
            style={{ borderColor: '#E2DFD8', background: '#FBF9F6' }}
          >
            {!isClosed && (
              <button
                type="button"
                onClick={() => addRow(sheet)}
                className="text-xs px-3 py-1.5 rounded font-medium border"
                style={{ background: 'white', color: '#2B5339', borderColor: '#2B5339' }}
                data-testid="prod-add-row"
              >+ Add sub-brand row</button>
            )}
            {!isClosed && (
              <>
                <Button size="sm" variant="outline" onClick={() => saveSheet(sheet)} disabled={isSheetBusy} data-testid="prod-save" title="Save row edits">
                  <Save className="w-3 h-3 sm:mr-1" /><span className="hidden sm:inline">Save</span>
                </Button>
                <Button size="sm" variant="outline" onClick={() => syncToStock(sheet)} disabled={isSheetBusy} data-testid="prod-sync-stock" title="Save & post Actual Production to Stock">
                  <Upload className="w-3 h-3 sm:mr-1" /><span className="hidden sm:inline">Sync to Stock</span>
                </Button>
                <Button size="sm" onClick={() => closeSheet(sheet)} disabled={isSheetBusy} style={{ background: '#2B5339' }} data-testid="prod-close" title="Close & lock this lot">
                  <Lock className="w-3 h-3 sm:mr-1" /><span className="hidden sm:inline">Close & Lock</span>
                </Button>
                <button
                  type="button"
                  onClick={() => deleteSheet(sheet)}
                  disabled={isSheetBusy}
                  className="text-xs px-2 py-1.5 rounded font-medium border hover:bg-[#FFF1EC] flex items-center"
                  style={{ color: '#C05C35', borderColor: '#C05C35' }}
                  title="Delete this lot. Only allowed if stock has not been synced yet."
                  data-testid="prod-delete"
                ><Trash2 className="w-3 h-3 sm:mr-1" /><span className="hidden sm:inline">Delete</span></button>
              </>
            )}
            {isClosed && (
              <Button size="sm" variant="outline" onClick={() => reopenSheet(sheet)} disabled={isSheetBusy} data-testid="prod-reopen" title="Reopen this lot">
                <Unlock className="w-3 h-3 sm:mr-1" /><span className="hidden sm:inline">Reopen</span>
              </Button>
            )}
          </div>
        </>
        )}
      </Card>
    );
  };

  return (
    <div data-testid="production-section">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <h2 className="text-2xl md:text-3xl font-bold" style={{ color: '#2B5339' }}>Production Planner</h2>
      </div>

      <Card className="p-4 bg-white border mb-4" style={{ borderColor: '#E2DFD8' }}>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-xs font-medium block mb-1" style={{ color: '#5C6B61' }}>Date</label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-44" data-testid="prod-date" />
          </div>
          <div>
            <label className="text-xs font-medium block mb-1" style={{ color: '#5C6B61' }}>Facility</label>
            <div className="flex gap-1.5">
              {['all', 'E-302', 'E-303'].map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFacility(f)}
                  className="text-xs px-3 py-2 rounded font-medium border transition-colors h-10"
                  style={facility === f
                    ? { background: '#2B5339', color: 'white', borderColor: '#2B5339' }
                    : { background: 'white', color: '#2B5339', borderColor: '#E2DFD8' }}
                  data-testid={`prod-facility-${f}`}
                >
                  {f === 'all' ? 'All' : f}
                </button>
              ))}
            </div>
          </div>
          <Button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="h-10 px-3"
            style={{ background: '#2B5339' }}
            data-testid="prod-create-open"
          >
            + Create Lot
          </Button>
          {/* Expand / Collapse All — operators want a one-click way to open
              every lot card in view (or close them after a bulk review). When
              any sheet is currently collapsed the button reads "Expand all"
              and opens every sheet currently in `sheets`; otherwise it reads
              "Collapse all" and resets the expanded map. */}
          {sheets.length > 0 && (() => {
            const allOpen = sheets.length > 0 && sheets.every((s) => !!expanded[s.id]);
            return (
              <Button
                type="button"
                variant="outline"
                className="h-10 px-3"
                style={{ color: '#2B5339', borderColor: '#2B5339' }}
                onClick={() => {
                  if (allOpen) {
                    setExpanded({});
                  } else {
                    const next = {};
                    sheets.forEach((s) => { next[s.id] = true; });
                    setExpanded(next);
                  }
                }}
                data-testid="prod-expand-toggle"
                title={allOpen ? 'Collapse every lot card' : 'Expand every lot card'}
              >
                {allOpen ? 'Collapse all' : 'Expand all'}
              </Button>
            );
          })()}
        </div>
      </Card>

      {loading ? (
        <div className="p-8 text-center"><Loader2 className="inline w-5 h-5 animate-spin" /></div>
      ) : sheets.length === 0 ? (
        <Card className="p-8 text-center" style={{ color: '#5C6B61' }}>
          No lot sheets for this date/facility. Set <b>lots</b> in <b>Production Planning</b> to auto-create them, or click <b>+ Create Lot</b> to add one manually.
        </Card>
      ) : facilityBuckets ? (
        // Default view with "All" facility selected — bucket by facility, each
        // lot card listed independently (no parent grouping) inside its facility.
        // The lot list is wrapped in overflow-x-auto + a min-w on its inner div
        // so Row 1 + Row 2 stay single-line (mirrors Order Management's wide table).
        <div className="space-y-6">
          {facilityBuckets.map((bucket) => (
            <div key={bucket.facility} data-testid={`prod-facility-section-${bucket.facility}`} className="space-y-3 relative">
              <div
                className="flex items-center justify-between flex-wrap gap-2 px-3 py-2 rounded border sticky top-0 z-30 shadow-md"
                style={{ background: '#2B5339', borderColor: '#2B5339', WebkitPosition: 'sticky' }}
              >
                <h3 className="text-base md:text-lg font-bold tracking-wide" style={{ color: 'white' }}>Facility · {bucket.facility}</h3>
                <span className="text-xs font-medium px-2 py-0.5 rounded" style={{ background: 'white', color: '#2B5339' }}>{bucket.sheets.length} lot{bucket.sheets.length !== 1 ? 's' : ''}</span>
              </div>
              <div className="overflow-x-auto pb-1">
                <div className="space-y-8 min-w-[900px]">
                  {bucket.sheets.map((s) => renderLotSheet(s))}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        // Single-facility selected — flat list of independent lot cards, sorted by Seq.
        // The sticky green facility header is rendered here too so operators always
        // see WHICH facility they're scoped to (parity with the "All" view).
        <div className="space-y-3 relative">
          <div
            className="flex items-center justify-between flex-wrap gap-2 px-3 py-2 rounded border sticky top-0 z-30 shadow-md"
            style={{ background: '#2B5339', borderColor: '#2B5339', WebkitPosition: 'sticky' }}
            data-testid={`prod-facility-section-${facility}`}
          >
            <h3 className="text-base md:text-lg font-bold tracking-wide" style={{ color: 'white' }}>Facility · {facility}</h3>
            <span className="text-xs font-medium px-2 py-0.5 rounded" style={{ background: 'white', color: '#2B5339' }}>{sheets.length} lot{sheets.length !== 1 ? 's' : ''}</span>
          </div>
          <div className="overflow-x-auto pb-1">
            <div className="space-y-8 min-w-[900px]">
              {sheets
                .slice()
                .sort((a, b) => seqOf(a) - seqOf(b))
                .map((s) => renderLotSheet(s))}
            </div>
          </div>
        </div>
      )}
      <p className="text-[11px] mt-3" style={{ color: '#5C6B61' }}>
        Lots auto-arrange by <b>Seq</b>. Each card shows two rows by default — click anywhere on Row 1 (the white strip with Seq / Parent / Lot) to expand the product form. Seq numbers must be unique; if you set a Seq that another lot already has, they automatically swap. Click <b>+ Create Lot</b> to add a manual lot sheet outside the auto-planner flow.
      </p>

      {/* CREATE LOT DIALOG — manually add a new lot sheet to the selected date.
          lot_no is auto-computed as next-available for (date × facility × parent). */}
      <Dialog open={createOpen} onOpenChange={(o) => { if (!o) setCreateOpen(false); }}>
        <DialogContent className="max-w-md" data-testid="prod-create-dialog">
          <DialogHeader>
            <DialogTitle>Create a new Lot Sheet</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2 text-sm">
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: '#5C6B61' }}>Date</label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-full" data-testid="prod-create-date" />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: '#5C6B61' }}>Facility</label>
              <div className="flex gap-1.5">
                {['E-302', 'E-303'].map((f) => (
                  <button
                    key={f}
                    type="button"
                    onClick={() => setCreateForm((p) => ({ ...p, facility: f }))}
                    className="text-xs px-3 py-2 rounded font-medium border h-10"
                    style={createForm.facility === f
                      ? { background: '#2B5339', color: 'white', borderColor: '#2B5339' }
                      : { background: 'white', color: '#2B5339', borderColor: '#E2DFD8' }}
                    data-testid={`prod-create-fac-${f}`}
                  >{f}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: '#5C6B61' }}>Parent Product</label>
              <select
                value={createForm.parent_product_id}
                onChange={(e) => setCreateForm((p) => ({ ...p, parent_product_id: e.target.value }))}
                className="w-full px-2 py-2 border rounded text-sm bg-white"
                style={{ borderColor: '#E2DFD8' }}
                data-testid="prod-create-parent"
              >
                <option value="">— Select Parent Product —</option>
                {[...products]
                  .filter(p => !p.is_virtual)
                  .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                  .map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: '#5C6B61' }}>Batches (optional)</label>
              <Input
                type="number"
                min="0"
                value={createForm.total_batches}
                onChange={(e) => setCreateForm((p) => ({ ...p, total_batches: e.target.value }))}
                className="w-full"
                data-testid="prod-create-batches"
              />
              <p className="text-[10px] mt-1" style={{ color: '#5C6B61' }}>Can be edited later on the lot card.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={createBusy} data-testid="prod-create-cancel">Cancel</Button>
            <Button onClick={submitCreate} disabled={createBusy} style={{ background: '#2B5339' }} data-testid="prod-create-submit">
              {createBusy ? 'Creating…' : 'Create Lot'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
